import { AbstractRectangleValuedItem } from "./AbstractRectangleValuedItem";
import { IItem } from "./IItem";
import { PatientProfileOptionKinds } from "../PatientProfileOptionKinds";
import { IHisInformationApplicable } from "./IHisInformationApplicable";
import { HisItemParameter } from "../../his/behavior/HisItemParameter";
import { IExternalScriptAssociatable } from "./IExternalScriptAssociatable";
import { IRelatedExternalScriptExecutable } from "./IRelatedExternalScriptExecutable";
import { IPatientProfileInformationApplicable } from "./IPatientProfileInformationApplicable";
import { PatientProfileInformation } from "../../patient/PatientProfileInformation";
import { PatientProfileItem } from "../../patient/PatientProfileItem";
import { HisInformationTextValue, IHisInformationValue } from "../../his/HisInformationValue";
import { ICheckable } from "./ICheckable";
import { IPasteRequestable } from "./IPasteRequestable";
import { IPasteApplicable } from "./IPasteApplicable";
import { RelationalActionParameter } from "./RelationalActionParameter";
import { IEmptyValidationSuppressible } from ".";
import { ScreenKinds } from "../../his/behavior";
import { ObjectId } from "../ObjectId";
import { INaturalTextSpecifiable } from "./INaturalTextSpecifiable";
import { IClickChainApplicable } from "./IClickChainApplicable";

export class CheckableItemValueSource {
  constructor(public isChecked: boolean, public checkedValue: number, public uncheckedValue: number) {
  }

  get value(): number {
    return this.isChecked
      ? this.checkedValue
      : this.uncheckedValue;
  }

  clone(): CheckableItemValueSource {
    return new CheckableItemValueSource(this.isChecked, this.checkedValue, this.uncheckedValue);
  }
}

export abstract class AbstractCheckableItem extends AbstractRectangleValuedItem implements
  ICheckable,
  IHisInformationApplicable,
  IExternalScriptAssociatable,
  IRelatedExternalScriptExecutable,
  IPatientProfileInformationApplicable,
  IPasteRequestable,
  IPasteApplicable,
  IEmptyValidationSuppressible,
  INaturalTextSpecifiable,
  IClickChainApplicable {
  constructor(typeKey: string, valueSource: CheckableItemValueSource) {
    super(typeKey, valueSource.value);
    this._originalValueSource = valueSource.clone();
    this._valueSource = valueSource.clone();
  }

  protected readonly _originalValueSource: CheckableItemValueSource;

  get originalValueSource(): CheckableItemValueSource {
    return this._originalValueSource;
  }

  private _valueSource: CheckableItemValueSource;

  handleItemUpdated(
    originalItem: IItem,
    updatedItem: IItem,
    isInSameSection: boolean,
    itemAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem,
    itemsAccessor: (finder?: ObjectId | string | ((i: IItem) => boolean), isSameSectionOnly?: boolean) => IItem[]
  ): IItem {
    return this.getUpdatedClone<AbstractCheckableItem>(
      s => super.handleItemUpdated(originalItem, updatedItem, isInSameSection, itemAccessor, itemsAccessor) as AbstractCheckableItem,
      s => s.createMultiCheckableUpdatedClone(isInSameSection, updatedItem),
      s => s.createCheckedUpdatedClone(isInSameSection, updatedItem),
    );
  }

  private createMultiCheckableUpdatedClone(isInSameSection: boolean, updatedItem: IItem): AbstractCheckableItem {
    return ((updatedItem instanceof AbstractCheckableItem) && this.isInSameScope(updatedItem, isInSameSection) && this.isInSameGroup(updatedItem) && (this.isMultiCheckable !== updatedItem.isMultiCheckable))
      ? this.createUpdatedClone<AbstractCheckableItem>(s => s.isMultiCheckable = updatedItem.isMultiCheckable)
      : null;
  }

  private createCheckedUpdatedClone(isInSameSection: boolean, updatedItem: IItem): AbstractCheckableItem {
    return ((updatedItem instanceof AbstractCheckableItem) && this.isInSameScope(updatedItem, isInSameSection) && this.isInSameGroup(updatedItem) && !this.isMultiCheckable && updatedItem.isChecked)
      ? this.createUpdatedClone<AbstractCheckableItem>(s => s.isChecked = false)
      : null;
  }

  private isInSameScope(updatedItem: AbstractCheckableItem, isInSameSection: boolean): boolean {
    if (updatedItem.shouldLinkAsWideScope !== this.shouldLinkAsWideScope) { return false; }
    return this.shouldLinkAsWideScope || isInSameSection;
  }

  private isInSameGroup(updatedItem: AbstractCheckableItem): boolean {
    return (!!this.groupKey && (this.groupKey !== "") && (updatedItem.groupKey === this.groupKey))
      && (!!this.typeKey && (this.typeKey !== "") && (updatedItem.typeKey === this.typeKey));
  }

  get isChecked(): boolean {
    return this._valueSource.isChecked;
  }

  set isChecked(value: boolean) {
    this._valueSource.isChecked = value;
    this.updateValue();
  }

  get isEmpty(): boolean {
    return !this.isChecked;
  }

  get checkedValue(): number {
    return this._valueSource.checkedValue;
  }

  set checkedValue(value: number) {
    this._valueSource.checkedValue = value;
    this.updateValue();
  }

  get uncheckedValue(): number {
    return this._valueSource.uncheckedValue;
  }

  set uncheckedValue(value: number) {
    this._valueSource.uncheckedValue = value;
    this.updateValue();
  }

  updateValue(): void {
    this.value = this._valueSource.value;
  }

  public isHisInfo: boolean = false;
  public displayName: string = null;
  public hisItemParameter: HisItemParameter = null;
  public groupKey: string = "";
  public isMultiCheckable: boolean = false;
  public enableNameGroupEmptyCheck: boolean = false;
  public enableEmptyCheckConfirmation: boolean = false;
  /**
   * 患者プロフィールに反映させる文字列
   */
  public text: string = null;
  public patientProfileOption: PatientProfileOptionKinds = null;
  public itemName: string = "";

  /** 自然言語 */
  public isNaturalViewEnable: boolean;
  public naturalText: string = null;  // text-paste対応で必要

  public pasteDestinations: RelationalActionParameter[] = [];
  public parentAlertInformations: RelationalActionParameter[] = [];

  /** グループ名称。一括入力時の識別子。 同一の識別子を持つ項目に対して値を入力する。 */
  public groupName?: string;
  /** 選択画面で複数選択可能か */
  public isDialogMultiSelectable: boolean;
  /** 登録、または一時保存までの編集中の間、選択されている項目を保持する */
  public selectedItems: any[] = null;
  /** フリー入力（チェックを付ける）可否 */
  public isFreeInputable: boolean;
  /**
   * 入力方法がフリー入力（チェックを付ける）か。
   * フリー入力：true, HIS情報選択：false
   */
  public shouldInputFree: boolean;
  /** ページをまたいで単一選択の制御するかどうか */
  public shouldLinkAsWideScope: boolean;

  public dacsProcessType: string = null;
  public dacsItemName: string = null;
  public dacsItemCode: string = null;

  protected applyMembers(clone: AbstractCheckableItem): void {
    super.applyMembers(clone);
    clone.checkedValue = this.cloneValue(this.checkedValue);
    clone.uncheckedValue = this.cloneValue(this.uncheckedValue);
    clone.isChecked = this.cloneValue(this.isChecked);
    clone.isHisInfo = this.cloneValue(this.isHisInfo);
    clone.displayName = this.cloneValue(this.displayName);
    clone.hisItemParameter = this.cloneValue(this.hisItemParameter);
    clone.groupKey = this.cloneValue(this.groupKey);
    clone.isMultiCheckable = this.cloneValue(this.isMultiCheckable);
    clone.enableNameGroupEmptyCheck = this.cloneValue(this.enableNameGroupEmptyCheck);
    clone.enableEmptyCheckConfirmation = this.cloneValue(this.enableEmptyCheckConfirmation);
    clone.text = this.cloneValue(this.text);
    clone.patientProfileOption = this.cloneValue(this.patientProfileOption);
    clone.itemName = this.cloneValue(this.itemName);
    clone.isNaturalViewEnable = this.cloneValue(this.isNaturalViewEnable);
    clone.naturalText = this.cloneValue(this.naturalText);
    clone.pasteDestinations = this.cloneValue(this.pasteDestinations);
    clone.parentAlertInformations = this.cloneValue(this.parentAlertInformations);
    clone.groupName = this.cloneValue(this.groupName);
    clone.isDialogMultiSelectable = this.cloneValue(this.isDialogMultiSelectable);
    clone.selectedItems = this.cloneValue(this.selectedItems);
    clone.isFreeInputable = this.cloneValue(this.isFreeInputable);
    clone.shouldInputFree = this.cloneValue(this.shouldInputFree);
    clone.shouldLinkAsWideScope = this.cloneValue(this.shouldLinkAsWideScope);
    clone.dacsProcessType = this.cloneValue(this.dacsProcessType);
    clone.dacsItemName = this.cloneValue(this.dacsItemName);
    clone.dacsItemCode = this.cloneValue(this.dacsItemCode);
  }

  setHisInfoItems(hisInfoValue: IHisInformationValue, shouldSkipInputMethodKindUpdate: boolean): void {
    if (!this.hasText) { return; }
    const values = (hisInfoValue instanceof HisInformationTextValue) ? hisInfoValue.data : [];
    const value = values.join("・"); // v1では"・"が区切り文字に設定されている
    this.isChecked = (this.text === value);
  }

  get hasText(): boolean {
    return (this.text !== null) && (this.text !== undefined) && (this.text !== "");
  }

  /**
   * V1の挙動を確認すると、ScreenKinds.NoneのHIS項目の選択オブジェクトは手動選択が可能な模様
   */
  get isNoDialogHisItem(): boolean {
    return (this.hisItemParameter && this.hisItemParameter.screenKind === ScreenKinds.None);
  }

  getValueForExternalScript(): string {
    return this.isChecked ? "1" : "0";
  }

  setValueFromExternalScript(value: string): void {
    this.isChecked = (value === "1");
  }

  handlePatientProfileInformationApplied(info: PatientProfileInformation): boolean {
    if (!info || !info.items) { return false; }
    if (!this.name || !this.isEditable || this.isHisInfo) { return false; }
    const found = info.items.find(item => this.isPatientProfileKeyMatched(item.name));
    if (!found) { return false; }

    switch (this.patientProfileOption) {
    case PatientProfileOptionKinds.Boolean:
      if (found.value && (found.value.toUpperCase() === "TRUE")) {
        this.isChecked = true;
        return true;
      }
      if (found.value && (found.value.toUpperCase() === "FALSE")) {
        this.isChecked = false;
        return true;
      }
      return false;
    case PatientProfileOptionKinds.InitialValue:
    case PatientProfileOptionKinds.InitialValueEmpty:
      this.isChecked = (this.text === found.value);
      return true;
    }
    return false;
  }

  public isPatientProfileKeyMatched(key: string): boolean {
    if (!key) { return false; }
    return key === this.getPatientProfileKey();
  }

  private getPatientProfileKey(): string {
    if (this.isHisInfo || !this.name || !this.patientProfileOption) { return null; }
    
    if (this.patientProfileOption === PatientProfileOptionKinds.Boolean) {
      return !!this.itemName
        ? this.name + "_" + this.itemName
        : null;
    }
    if (this.patientProfileOption === PatientProfileOptionKinds.InitialValue || this.patientProfileOption === PatientProfileOptionKinds.InitialValueEmpty) {
      return this.name;
    }

    // 未実装
    console.warn("unknown PatientProfileOption." + this.patientProfileOption);
    return null;
  }

  public createPatientProfileItem(): PatientProfileItem {
    const key = this.getPatientProfileKey();
    if (!key) { return null; }

    const text = this.getPatientProfileTextValue();
    if (text == null) { return null; }

    const item = new PatientProfileItem();
    item.name = key;
    item.value = text;
    return item;
  }

  private getPatientProfileTextValue(): string {
    switch (this.patientProfileOption) {
    case PatientProfileOptionKinds.Boolean:
      return this.isChecked ? "TRUE" : "FALSE";
    case PatientProfileOptionKinds.InitialValue:
      return this.isChecked ? this.text : null;
    case PatientProfileOptionKinds.InitialValueEmpty:
      return (this.isChecked && this.text) ? this.text : "";
    }
    console.warn("unknown PatientProfileOption." + this.patientProfileOption);
    return undefined;
  }

  adjustFontSize(): void {
    // DO NOTHING
  }

  applyPastedValue(text: string, isChecked: boolean): void {
    if (isChecked === null || isChecked === undefined) {
      // TODO:V1の仕様の確認
      // 現状の動作を確認して、それっぽい動作に合わせこんでいる
      this.isChecked = (this.text === text);
      return;
    }
    this.isChecked = isChecked;
  }

  isPasteApplicable(isQuoting: boolean): boolean {
    // チェックからチェックへの文書内転記が仕込んであるフォーマットで引用・転記すると、
    // 転記元の文書のユーザ操作可能な状態のチェックの状態を再現できないので対策している
    if (!isQuoting) { return true; }
    return !this.isFreeInputable;
  }

  pasteTo(item: IPasteApplicable): void {
    // チェックを外したときはテキストとしては空文字を反映させる
    // そもそもV1ではテキストの入れ替えではなく、末尾に追加していく挙動になっている
    // 末尾に追加していく挙動にするべきかどうかは検討が必要
    const text = this.isChecked ? this.naturalText : "";
    item.applyPastedValue(text, this.isChecked);
  }

  isPasteKeyMatch(parameter: RelationalActionParameter): boolean {
    // TODO:V1の仕様の確認
    return (parameter.key === this.name && this.isSubKeyMatch(parameter.subKey));
  }

  isSubKeyMatch(subKey: string): boolean {
    if (subKey) {
      return (subKey === this.itemName);
    } else {
      return (!this.itemName || this.itemName === "");
    }
  }

  get isSuppressionActive(): boolean {
    return !this.isChecked;
  }

  isValueUpdated(item: any): boolean {
    if (!item) { return true; }
    return (this.isChecked !== item.isChecked);
  }

  setSelectedItems(items: any[]): void {
    this.selectedItems = items;
  }

  getDisplayName(): string {
    return this.isHisInfo ? this.displayName : this.name;
  }

  get currentNaturalText(): string {
    // 各Itemで実装
    return "";
  }

  get clickChainIdentifier(): string {
    return this.formatElementName + "#" + this.name + "#" + this.itemName;
  }
}
